'use client';

/**
 * ThemeProvider
 * ─────────────────────────────────────────────────────────────
 * Provides the current color palette via React Context and animates
 * between modes when setMode() is called.
 *
 * Usage:
 *   <ThemeProvider>
 *     <App />
 *   </ThemeProvider>
 *
 *   function MyComponent() {
 *     const { colors, setMode } = useTheme();
 *     return <div style={{ color: colors.text }}>Hi</div>;
 *   }
 *
 * Transition is done via requestAnimationFrame with RGB-space lerp.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { palettes, type ColorPalette, type ThemeMode, tabToMode } from './colors';
import { easeInOutCubic, isHex, lerpHex } from './colorInterpolation';
import { motion } from './tokens';

interface ThemeContextValue {
  /** Current (possibly mid-transition) color palette. */
  colors: ColorPalette;
  /** The target mode. Differs from actual colors during transitions. */
  mode: ThemeMode;
  /** Switch theme mode with animation. */
  setMode: (mode: ThemeMode) => void;
  /** Switch theme based on a tab key (home, food, training, …). */
  setModeByTab: (tabKey: string) => void;
  /** Whether a transition is currently playing. */
  isTransitioning: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Blend two palettes in RGB space. Non-color properties switch at midpoint.
 */
function blendPalettes(
  from: ColorPalette,
  to: ColorPalette,
  t: number
): ColorPalette {
  const result: Record<string, string> = {};
  const keys = Object.keys(to) as Array<keyof ColorPalette>;
  for (const key of keys) {
    const fv = from[key];
    const tv = to[key];
    if (isHex(fv) && isHex(tv)) {
      result[key] = lerpHex(fv, tv, t);
    } else {
      // Shadow strings and other non-hex values snap at midpoint.
      result[key] = t < 0.5 ? fv : tv;
    }
  }
  return result as unknown as ColorPalette;
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Initial mode. Defaults to "dark" (avoids a cream FOUC on first paint). */
  initialMode?: ThemeMode;
  /** Transition duration in ms. Defaults to 500. */
  duration?: number;
}

export function ThemeProvider({
  children,
  initialMode = 'dark',
  duration = motion.duration.slow,
}: ThemeProviderProps) {
  const [colors, setColors] = useState<ColorPalette>(palettes[initialMode]);
  const [mode, setModeState] = useState<ThemeMode>(initialMode);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const rafRef = useRef<number | null>(null);
  const currentColorsRef = useRef<ColorPalette>(palettes[initialMode]);

  // Keep ref in sync with state so we always animate from the *visible* color
  useEffect(() => {
    currentColorsRef.current = colors;
  }, [colors]);

  const setMode = useCallback(
    (nextMode: ThemeMode) => {
      setModeState(prev => {
        if (prev === nextMode) return prev;

        const from = { ...currentColorsRef.current };
        const to = palettes[nextMode];
        const start = performance.now();

        setIsTransitioning(true);

        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
        }

        const tick = (now: number) => {
          const raw = Math.min(1, (now - start) / duration);
          const t = easeInOutCubic(raw);
          setColors(blendPalettes(from, to, t));

          if (raw < 1) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            setColors({ ...to });
            setIsTransitioning(false);
            rafRef.current = null;
          }
        };

        rafRef.current = requestAnimationFrame(tick);
        return nextMode;
      });
    },
    [duration]
  );

  const setModeByTab = useCallback(
    (tabKey: string) => {
      const nextMode = tabToMode[tabKey] ?? 'neutral';
      setMode(nextMode);
    },
    [setMode]
  );

  // Clean up any in-flight animation on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const value: ThemeContextValue = {
    colors,
    mode,
    setMode,
    setModeByTab,
    isTransitioning,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Access the current theme. Must be inside <ThemeProvider>.
 *
 * @throws if used outside a ThemeProvider
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}

/**
 * Convenience hook when you only need the colors.
 * Avoids destructuring noise in components.
 */
export function useColors(): ColorPalette {
  return useTheme().colors;
}
